import {
  EnrollmentAdditionalInformationRequest,
  StudentInfoAdminResponse,
  DormitorySpecialCaseResponse,
  EnrollmentHealthInsuranceRequest,
  PreferredForeignLanguageResponse,
  StudentInfoAdminRequest,
  StudentDormitoryDto,
  HealthInsuranceStatusRequest,
  PreferredForeignLanguageRequest,
  StudentDormitoryApplicationAdmissionRequest,
} from "../../models/dtos";
import { ApiResponse } from "../../services/base/apiResponse";
import { studentsApi } from "../../services/students/studentsApi";
import * as Yup from "yup";
import { FormikProvider, useFormik, getIn } from "formik";
import { RadioButton } from "primereact/radiobutton";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { foreignLanguagesApi } from "../../services/nomenclatures/foreignLanguagesApi";
import CustomCheckbox from "../../components/forms/CustomCheckbox";
import { dormitorySpecialCasesApi } from "../../services/nomenclatures/dormitorySpecialCasesApi";
import { useParams } from "react-router-dom";
import { Message } from "primereact/message";
import { Fieldset } from "primereact/fieldset";
import CustomInputText from "../../components/forms/CustomInputText";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { studentDormitoriesApi } from "../../services/studentDormitories/studentDormitoriesApi";
import { studentDocumentsApi } from "../../services/studentDocuments/studentDocumentsApi";

interface StudentDataProps {
  student?: ApiResponse<StudentInfoAdminResponse>;
}

const AdditionalInformation = ({ student }: StudentDataProps) => {
  const toast = useRef<Toast>(null);
  const [age, setAge] = useState<number | null>(null);
  const admissionApplicationId = student?.data.admissionApplicationId;
  const { studentId } = useParams();
  const { data: languages } = foreignLanguagesApi.useGetForeignLanguagesQuery(student?.data.studentId!);
  const { data: insurance } = studentsApi.useGetHealthInsuranceStatusQuery(student?.data.studentId!);
  const { data: foreignLang } = studentsApi.useGetPreferredLanguageQuery(student?.data.studentId!);
  const { data: dormitorySpecialCases } = dormitorySpecialCasesApi.useGetDormitorySpecialCasesQuery();

  if (!studentId) {
    return <Message text="Няма открит студент." severity="error"></Message>;
  }

  const { data: additionalInfo, isLoading, error } = studentsApi.useGetAdditionalInfoQuery(parseInt(studentId));

  const [
    updateStudentHealth,
    { isLoading: isUpdateStudentHealth, isError: isUpdateHealthError, isSuccess: isUpdateHealthSuccess },
  ] = studentsApi.useUpdateStudentHealthMutation();

  const [
    updateStudentLang,
    { isLoading: isUpdateStudentLang, isError: isUpdateLangError, isSuccess: isUpdateLangSuccess },
  ] = studentsApi.useUpdateStudentLangMutation();

  const [
    updateStudentBuildingRoom,
    {
      isLoading: isUpdateStudentBuildingRoom,
      isError: isUpdateBuildingRoomError,
      isSuccess: isUpdateBuildingRoomSuccess,
    },
  ] = studentDormitoriesApi.useUpdateStudentBuildingRoomMutation();

  const [
    createOrUpdateApplication,
    { isLoading: isUpdateStudentDormitory, isError: isUpdateDormitoryError, isSuccess: isUpdateDormitorySuccess },
  ] = studentDormitoriesApi.useCreateOrUpdateApplicationMutation();

  const parsedChildrenDetails = () => {
    if (additionalInfo?.data?.childrenDetails) {
      return JSON.parse(additionalInfo?.data.childrenDetails);
    }
  };

  useEffect(() => {
    const calculateAge = (birthdate: any) => {
      const birthDate = new Date(birthdate);
      const today = new Date();

      if (isNaN(birthDate.getTime())) return null;

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDifference = today.getMonth() - birthDate.getMonth();

      if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    };

    if (student?.data?.birthDate) {
      const birthDate = student.data.birthDate;
      const calculatedAge = calculateAge(birthDate);
      setAge(calculatedAge);
    }
  }, [student, setAge]);

  const formik = useFormik<Partial<StudentDormitoryApplicationAdmissionRequest>>({
    initialValues: {
      declarationType: additionalInfo?.data.declarationType,
      dormitorySpecialCaseId: additionalInfo?.data.dormitorySpecialCaseId,
      hasSibling: additionalInfo?.data.hasSibling,
      siblingEgn: additionalInfo?.data.siblingEgn,
      siblingNames: additionalInfo?.data.siblingNames,
      siblingBuilding: additionalInfo?.data.siblingBuilding,
      siblingRoom: additionalInfo?.data.siblingRoom,
      spouseEgn: additionalInfo?.data.spouseEgn,
      spouseNames: additionalInfo?.data.spouseNames,
      spouseMarriageNumber: additionalInfo?.data.spouseMarriageNumber,
      childrenDetails: additionalInfo?.data?.childrenDetails,
      childrenDetailsArray: parsedChildrenDetails() ?? [],
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      values.childrenDetails = JSON.stringify(values.childrenDetailsArray);
      createOrUpdateApplication({ studentId: student?.data?.studentId!, request: values }).then((response) => {
        if ("data" in response && response.data) {
          toast.current!.show({
            severity: "success",
            summary: "Успешно",
            detail: "Данните на студента са запазени.",
          });
        }
      });
    },
  });

  const formikHealth = useFormik({
    initialValues: {
      healthInsuranceStatus: insurance?.data.healthInsuranceStatus ? insurance?.data.healthInsuranceStatus : 0,
      healthInsuranceStatusUpdatedOn: null,
    },
    enableReinitialize: true,
    onSubmit: (values: HealthInsuranceStatusRequest) => {
      updateStudentHealth({ studentId: student?.data?.studentId!, request: values }).then((response) => {
        if ("data" in response && response.data) {
          toast.current!.show({ severity: "success", summary: "Успешно", detail: "Данните на студента са запазени." });
        }
      });
    },
  });

  const formikLang = useFormik({
    initialValues: { foreignLanguageId: foreignLang?.data.foreignLanguageId ? foreignLang?.data.foreignLanguageId : 0 },
    enableReinitialize: true,
    onSubmit: (values: PreferredForeignLanguageRequest) => {
      updateStudentLang({ studentId: student?.data?.studentId!, request: values }).then((response) => {
        if ("data" in response && response.data) {
          toast.current!.show({ severity: "success", summary: "Успешно", detail: "Данните на студента са запазени." });
        }
      });
    },
  });

  const formikDormitory = useFormik<Partial<StudentDormitoryDto>>({
    initialValues: {
      building: student?.data.studentDormitories?.length
        ? student.data.studentDormitories[student.data.studentDormitories.length - 1].building
        : undefined,
      room: student?.data.studentDormitories?.length
        ? student.data.studentDormitories[student.data.studentDormitories.length - 1].room
        : undefined,
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      updateStudentBuildingRoom({ studentId: parseInt(studentId) ?? 0, request: values }).then((response) => {
        if ("data" in response && response.data) {
          toast.current!.show({ severity: "success", summary: "Успешно", detail: "Данните на студента са запазени." });
        }
      });
    },
  });
  const handleAddChild = () => {
    formik.setFieldValue("childrenDetailsArray", [
      ...(formik.values.childrenDetailsArray ?? []),
      { name: "", egn: "" },
    ]);
  };

  return (
    <>
      <div className="flex justify-content-center flex-wrap row">
        {age != null && age <= 26 && (
          <div className="col-6">
            <Card className="w-full mt-4">
              <FormikProvider value={formikHealth}>
                <Divider />
                <div className="card">
                  {" "}
                  <div className="col-12 mb-4">
                    <h2>Информация за здравни осигуровки </h2>
                  </div>{" "}
                  <div className="flex align-items-center">
                    <RadioButton
                      inputId="healthInsuranceStatus_1"
                      name="healthInsuranceStatus"
                      value={1}
                      onChange={(e) => formikHealth.setFieldValue("healthInsuranceStatus", e.value)}
                      checked={formikHealth?.values.healthInsuranceStatus == 1}
                    />
                    <label htmlFor="health1" className="ml-2">
                      ДЕКЛАРИРА, ЧЕ ПОЛУЧАВА доходи от трудова дейност.
                    </label>
                  </div>
                  <div className="flex align-items-center mt-4">
                    <RadioButton
                      inputId="healthInsuranceStatus_2"
                      name="healthInsuranceStatus"
                      value={2}
                      onChange={(e) => formikHealth.setFieldValue("healthInsuranceStatus", e.value)}
                      checked={formikHealth?.values.healthInsuranceStatus == 2}
                    />
                    <label htmlFor="health2" className="ml-2">
                      ДЕКЛАРИРА, ЧЕ ПОЛУЧАВА наследствена пенсия.
                    </label>
                  </div>
                  <div className="flex align-items-center mt-4">
                    <RadioButton
                      inputId="healthInsuranceStatus_3"
                      name="healthInsuranceStatus"
                      value={3}
                      onChange={(e) => formikHealth.setFieldValue("healthInsuranceStatus", e.value)}
                      checked={formikHealth?.values.healthInsuranceStatus == 3}
                    />
                    <label htmlFor="health3" className="ml-2">
                      ДЕКЛАРИРА, ЧЕ ПОЛУЧАВА други доходи, върху които се дължат здравноосигурителни вноски по реда на
                      чл. 40, ал. 1 от 330.
                    </label>
                  </div>
                  <div className="flex align-items-center mt-4 mb-6">
                    <RadioButton
                      inputId="healthInsuranceStatus_4"
                      name="healthInsuranceStatus"
                      value={4}
                      onChange={(e) => formikHealth.setFieldValue("healthInsuranceStatus", e.value)}
                      checked={formikHealth?.values.healthInsuranceStatus == 4}
                    />
                    <label htmlFor="health4" className="ml-2">
                      ДЕКЛАРИРА, ЧЕ НЕ ПОЛУЧАВА нито един от изброените по-горе доходи.
                    </label>
                  </div>
                </div>{" "}
                <Button
                  label="Запази"
                  type="submit"
                  onClick={() => formikHealth.handleSubmit()}
                  disabled={isUpdateStudentHealth}
                  className="mt-4"
                />{" "}
              </FormikProvider>
            </Card>
          </div>
        )}
        {foreignLang != null && (
          <div className={age !== null && age > 27 ? "col-12" : "col-6"}>
            <Card className="w-full mt-4">
              <FormikProvider value={formikLang}>
                <Divider />
                <div className="col-12 mb-4">
                  <h2>Информация за чужд език</h2>
                </div>{" "}
                <div className="p-fluid p-grid p-justify-center">
                  <div className="p-col-12 p-md-6">
                    {languages &&
                      languages.data.map((lang) => (
                        <div key={lang.foreignLanguageId} className="flex align-items-center mt-4">
                          <RadioButton
                            inputId={`foreignLanguage${lang.foreignLanguageId}`}
                            name="foreignLanguageId"
                            value={lang.foreignLanguageId}
                            onChange={(e) => {
                              formikLang.setFieldValue("foreignLanguageId", e.value), console.log(e.value);
                            }}
                            checked={formikLang.values.foreignLanguageId === lang.foreignLanguageId}
                          />
                          <label htmlFor={`foreignLanguage${lang.foreignLanguageId}`} className="ml-2">
                            {lang.name}
                          </label>
                        </div>
                      ))}
                  </div>
                </div>
                <Button
                  label="Запази"
                  type="submit"
                  onClick={() => formikLang.handleSubmit()}
                  disabled={isUpdateStudentLang}
                  className="mt-4"
                />{" "}
              </FormikProvider>
            </Card>
          </div>
        )}
        <div className="col-12">
          <Card className="w-full mt-4">
            <Divider />
            <div className="card">
              {" "}
              <div className="col-12">
                <h2>Информация за общежитие</h2>
              </div>
              <FormikProvider value={formikDormitory}>
                <div className="col-12">
                  <Divider>
                    <h3 className="p-text-center" style={{ fontSize: 16, fontWeight: "bold", margin: "15px" }}>
                      Студентът е настанен в:
                    </h3>
                  </Divider>
                  <div className="card mt-2">
                    <div className="p-fluid formgrid grid">
                      <div className="field col-12 md:col-6">
                        <CustomInputText id="building" label="Сграда" />
                      </div>
                      <div className="field col-12 md:col-6">
                        <CustomInputText id="room" label="Стая" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Button
                      label="Запази данни за общежитие"
                      type="submit"
                      onClick={() => formikDormitory.handleSubmit()}
                      disabled={isUpdateStudentBuildingRoom}
                      className="mt-4"
                    />{" "}
                  </div>{" "}
                  <Divider />
                </div>
              </FormikProvider>
              <FormikProvider value={formik}>
                <h4>Тип на заявлението:</h4>
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="declarationType_1"
                    name="declarationType"
                    value={1}
                    onChange={(e) => formik.setFieldValue("declarationType", e.value)}
                    checked={formik.values.declarationType == 1}
                  />
                  <label htmlFor="declarationTypeId" className="ml-2">
                    Заявление за несемейни студенти
                  </label>
                </div>
                <div className="flex align-items-center mt-4">
                  <RadioButton
                    inputId="declarationType_2"
                    name="declarationType"
                    value={2}
                    onChange={(e) => formik.setFieldValue("declarationType", e.value)}
                    checked={formik.values.declarationType == 2}
                  />
                  <label htmlFor="health2" className="ml-2">
                    Заявление за семейни студенти
                  </label>
                </div>
                <div className="flex align-items-center mt-4">
                  <RadioButton
                    inputId="declarationType_3"
                    name="declarationType"
                    value={3}
                    onChange={(e) => formik.setFieldValue("declarationType", e.value)}
                    checked={formik.values.declarationType == 3}
                  />
                  <label htmlFor="health3" className="ml-2">
                    Не желае да кандидатства за общежитие
                  </label>
                </div>
                {formik.values.declarationType === 1 && (
                  <>
                    <div className="card mt-6">
                      <Fieldset legend="Без класиране (по право) като:">
                        {dormitorySpecialCases &&
                          dormitorySpecialCases.data
                            .filter((x) => x.declarationType === 1)
                            ?.map((dormitorySpecialCase) => (
                              <div
                                key={dormitorySpecialCase.dormitorySpecialCaseId}
                                className="p-field-radiobutton flex align-items-center mt-4"
                              >
                                <RadioButton
                                  inputId={`dormitorySpecialCase${dormitorySpecialCase.dormitorySpecialCaseId}`}
                                  name="dormitorySpecialCaseId"
                                  value={dormitorySpecialCase.dormitorySpecialCaseId}
                                  onChange={(e) => formik.setFieldValue("dormitorySpecialCaseId", Number(e.value))}
                                  checked={
                                    formik.values.dormitorySpecialCaseId === dormitorySpecialCase.dormitorySpecialCaseId
                                  }
                                />
                                <label htmlFor={`dormitorySpecialCase${dormitorySpecialCase.dormitorySpecialCaseId}`}>
                                  {dormitorySpecialCase.name}
                                </label>
                              </div>
                            ))}
                        <div className="mt-4">
                          <CustomCheckbox
                            name="hasSibling"
                            id="hasSibling"
                            label="ДЕКЛАРИРАМ, ЧЕ имам брат/сестра - студент/ка в Тракийски университет, Стара Загора, живеещ в общежитие"
                            value={formik.values.hasSibling}
                            checked={formik.values.hasSibling || false}
                            onChange={formik.handleChange}
                          />
                        </div>
                        {formik.values.hasSibling === true && (
                          <>
                            <Divider>
                              <h3
                                className="p-text-center"
                                style={{ fontSize: 16, fontWeight: "bold", margin: "15px" }}
                              >
                                Данни за брат/сестра:
                              </h3>
                            </Divider>
                            <div className="p-fluid">
                              <div className="p-field p-col-12">
                                <CustomInputText
                                  id="siblingNames"
                                  label="Три имена на брат/сестра"
                                  name="siblingNames"
                                  value={formik.values.siblingNames}
                                  onChange={formik.handleChange}
                                  className={
                                    formik.touched.siblingNames && formik.errors.siblingNames ? "p-invalid" : ""
                                  }
                                />
                                {formik.touched.siblingNames && formik.errors.siblingNames && (
                                  <small className="p-error">{formik.errors.siblingNames}</small>
                                )}
                              </div>
                              <div className="p-field p-col-12">
                                <CustomInputText
                                  id="siblingEgn"
                                  name="siblingEgn"
                                  label="ЕГН"
                                  value={formik.values.siblingEgn}
                                  onChange={formik.handleChange}
                                  className={formik.touched.siblingEgn && formik.errors.siblingEgn ? "p-invalid" : ""}
                                />
                                {formik.touched.siblingEgn && formik.errors.siblingEgn && (
                                  <small className="p-error">{formik.errors.siblingEgn}</small>
                                )}
                              </div>
                              <div className="p-field p-col-12">
                                <CustomInputText
                                  id="siblingBuilding"
                                  name="siblingBuilding"
                                  label="Сграда"
                                  value={formik.values.siblingBuilding}
                                  onChange={formik.handleChange}
                                  className={
                                    formik.touched.siblingBuilding && formik.errors.siblingBuilding ? "p-invalid" : ""
                                  }
                                />
                                {formik.touched.siblingBuilding && formik.errors.siblingBuilding && (
                                  <small className="p-error">{formik.errors.siblingBuilding}</small>
                                )}
                              </div>
                              <div className="p-field p-col-12">
                                <CustomInputText
                                  id="siblingRoom"
                                  name="siblingRoom"
                                  label="Стая"
                                  value={formik.values.siblingRoom}
                                  onChange={formik.handleChange}
                                  className={formik.touched.siblingRoom && formik.errors.siblingRoom ? "p-invalid" : ""}
                                />
                                {formik.touched.siblingRoom && formik.errors.siblingRoom && (
                                  <small className="p-error">{formik.errors.siblingRoom}</small>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </Fieldset>
                    </div>
                  </>
                )}
                {formik.values.declarationType === 2 && (
                  <>
                    <div className="card mt-6">
                      <Fieldset legend="Без класиране (по право) като:">
                        {dormitorySpecialCases &&
                          dormitorySpecialCases.data
                            .filter((x) => x.declarationType === 2)
                            ?.map((dormitorySpecialCase) => (
                              <div
                                key={dormitorySpecialCase.dormitorySpecialCaseId}
                                className="p-field-radiobutton flex align-items-center mt-4"
                              >
                                <RadioButton
                                  inputId={`dormitorySpecialCase${dormitorySpecialCase.dormitorySpecialCaseId}`}
                                  name="dormitorySpecialCaseId"
                                  value={dormitorySpecialCase.dormitorySpecialCaseId}
                                  onChange={(e) => formik.setFieldValue("dormitorySpecialCaseId", Number(e.value))}
                                  checked={
                                    formik.values.dormitorySpecialCaseId === dormitorySpecialCase.dormitorySpecialCaseId
                                  }
                                />
                                <label htmlFor={`dormitorySpecialCase${dormitorySpecialCase.dormitorySpecialCaseId}`}>
                                  {dormitorySpecialCase.name}
                                </label>
                              </div>
                            ))}
                        <Divider>
                          <h3 className="p-text-center" style={{ fontSize: 16, fontWeight: "bold", margin: "15px" }}>
                            Данни за съпруг/а:
                          </h3>
                        </Divider>
                        <div className="p-fluid">
                          <div className="p-field p-col-12">
                            <CustomInputText id="spouseNames" label="Три имена на съпруг/а" />
                            {formik.touched.spouseNames && formik.errors.spouseNames && (
                              <small className="p-error">{formik.errors.spouseNames}</small>
                            )}
                          </div>
                          <div className="p-field p-col-12">
                            <CustomInputText
                              id="spouseEgn"
                              label="ЕГН на съпруг/а"
                              value={formik.values.spouseEgn}
                              onChange={formik.handleChange}
                              className={formik.touched.spouseEgn && formik.errors.spouseEgn ? "p-invalid" : ""}
                            />
                            {formik.touched.spouseEgn && formik.errors.spouseEgn && (
                              <small className="p-error">{formik.errors.spouseEgn}</small>
                            )}
                          </div>
                          <div className="p-field p-col-12">
                            <CustomInputText
                              id="spouseMarriageNumber"
                              label="№ на свидетелство за граждански брак"
                              value={formik.values.spouseMarriageNumber}
                              onChange={formik.handleChange}
                              className={
                                formik.touched.spouseMarriageNumber && formik.errors.spouseMarriageNumber
                                  ? "p-invalid"
                                  : ""
                              }
                            />
                            {formik.touched.spouseMarriageNumber && formik.errors.spouseMarriageNumber && (
                              <small className="p-error">{formik.errors.spouseMarriageNumber}</small>
                            )}
                          </div>
                          <Divider>
                            <h3 className="p-text-center" style={{ fontSize: 16, fontWeight: "bold", margin: "15px" }}>
                              Данни за децата:
                            </h3>
                          </Divider>
                          {formik.values.childrenDetailsArray &&
                            formik.values.childrenDetailsArray.map((child, index) => (
                              <div className="p-grid" key={index}>
                                <div className="p-field p-col-8">
                                  <CustomInputText
                                    id={`childrenDetailsArray.${index}.name`}
                                    label="Три имена"
                                    className={
                                      getIn(formik.errors, `childrenDetailsArray.${index}.name`) ? "p-invalid" : ""
                                    }
                                  />
                                  {getIn(formik.errors, `childrenDetailsArray.${index}.name`) && (
                                    <small className="p-error">
                                      {getIn(formik.errors, `childrenDetailsArray.${index}.name`)}
                                    </small>
                                  )}
                                </div>
                                <div className="p-field p-col-4">
                                  <CustomInputText
                                    id={`childrenDetailsArray.${index}.egn`}
                                    label="EГН"
                                    className={
                                      getIn(formik.errors, `childrenDetailsArray.${index}.egn`) ? "p-invalid" : ""
                                    }
                                  />
                                  {getIn(formik.errors, `childrenDetailsArray.${index}.egn`) && (
                                    <small className="p-error">
                                      {getIn(formik.errors, `childrenDetailsArray.${index}.egn`)}
                                    </small>
                                  )}
                                  <Divider />
                                </div>
                              </div>
                            ))}
                          <Button
                            type="button"
                            label="+ Добави дете"
                            className="p-button-outlined p-mt-3 p-mb-2"
                            onClick={handleAddChild}
                          />
                        </div>
                      </Fieldset>
                    </div>
                  </>
                )}
                <Button
                  label="Запази"
                  type="submit"
                  onClick={() => formik.handleSubmit()}
                  disabled={isUpdateStudentDormitory}
                  className="mt-4"
                />{" "}
              </FormikProvider>
            </div>{" "}
          </Card>
        </div>
      </div>
    </>
  );
};

export default AdditionalInformation;
